/* friendlyVerdict — translates the rain-confidence engine's technical
 * signals into plain English a non-meteorologist can read in 5 seconds.
 *
 * The backend's `confidence.explanation` ships jargon like:
 *   "Pressure trending down 0.9 hPa — system approaching.
 *    CAPE 710 J/kg, LI -4.8 — convective storms possible.
 *    PW 47 mm — tropical column, rain capacity loaded."
 *
 * We turn that into:
 *   "Pressure is dropping — a weather system is moving in.
 *    The atmosphere has the energy for thunderstorms, and there's
 *    plenty of moisture overhead if storms fire."
 *
 * Translation reads each signal in `confidence.signals` rather than
 * regexing the technical paragraph — more reliable, supports new
 * signals without breaking.
 */

const FRIENDLY = {
  pressure: {
    drop_strong: "Air pressure is falling fast — a storm system is sliding in.",
    drop_mild: "Air pressure is easing down — a weather system may be approaching.",
    rise_strong: "Air pressure is climbing — clearer skies on the way.",
    rise_mild: "Air pressure is gently rising — generally settled.",
    flat: "Air pressure is steady — no big shift in the weather pattern.",
  },
  cape: {
    high: "The atmosphere has loads of energy — thunderstorms can fire fast.",
    moderate: "The atmosphere has enough energy for thunderstorms today.",
    low: "The atmosphere is stable — storms unlikely to develop on their own.",
  },
  pw: {
    tropical: "There's a lot of moisture overhead — any storm could dump heavy rain.",
    moist: "Decent moisture in the air — rain is possible if a storm fires.",
    dry: "Air is on the dry side — even if it rains, totals will be light.",
  },
  dewpoint: {
    sticky: "Air feels muggy — moisture is close to condensing.",
    comfortable: "Air feels comfortable — not loaded with moisture.",
    dry: "Air is dry — clouds will struggle to make rain.",
  },
  clouds: {
    overcast: "Sky is fully covered — limited sun expected.",
    partly: "Mix of sun and cloud — typical fair-weather setup.",
    clear: "Mostly clear skies overhead.",
  },
  minutely: {
    raining: "Rain is falling right now in your area.",
    arriving: "Rain showing up on the radar nearby — could reach you soon.",
    none: null, // hide when nothing useful to say
  },
};

function classifyPressure(signal) {
  const v = parseFloat(signal?.value || "0");
  if (v <= -1.5) return "drop_strong";
  if (v <= -0.4) return "drop_mild";
  if (v >= 1.5) return "rise_strong";
  if (v >= 0.4) return "rise_mild";
  return "flat";
}

function classifyCape(signal) {
  const m = (signal?.value || "").match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const v = parseFloat(m[1]);
  if (v >= 1500) return "high";
  if (v >= 500) return "moderate";
  return "low";
}

function classifyPw(signal) {
  const m = (signal?.value || "").match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const v = parseFloat(m[1]);
  if (v >= 45) return "tropical";
  if (v >= 30) return "moist";
  return "dry";
}

function classifyDewpoint(signal) {
  const m = (signal?.value || "").match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const v = parseFloat(m[1]);
  if (v <= 3) return "sticky";
  if (v <= 8) return "comfortable";
  return "dry";
}

function classifyMinutely(signal) {
  const v = (signal?.value || "").toLowerCase();
  const expl = (signal?.explanation || "").toLowerCase();
  if (expl.includes("now") && expl.includes("rain")) return "raining";
  if (expl.includes("incoming") || expl.includes("arriving") || expl.includes("approaching")) return "arriving";
  return "none";
}

const CLASSIFIERS = {
  pressure: classifyPressure,
  cape: classifyCape,
  pw: classifyPw,
  dewpoint: classifyDewpoint,
  minutely: classifyMinutely,
};

/**
 * Turn the confidence object into a 1-3 sentence plain-English explanation.
 * Order matters: minutely > pressure > cape > pw > dewpoint.
 * We pick the 2 most relevant non-null sentences to keep the line count
 * tight on the verdict screen.
 */
export function friendlyExplanation(confidence) {
  const signals = confidence?.signals || [];
  const byId = Object.fromEntries(signals.map((s) => [s.id, s]));
  const candidates = [];

  // Priority order — the first 2 non-null wins
  const order = ["minutely", "pressure", "cape", "pw", "dewpoint"];
  for (const id of order) {
    const sig = byId[id];
    if (!sig) continue;
    const classifier = CLASSIFIERS[id];
    if (!classifier) continue;
    const tone = classifier(sig);
    if (!tone) continue;
    const sentence = (FRIENDLY[id] || {})[tone];
    if (sentence) candidates.push(sentence);
    if (candidates.length >= 2) break;
  }

  if (!candidates.length) return null;
  return candidates.join(" ");
}
